import React, { useState, useRef, useEffect, useContext } from 'react';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { X, Send, Sparkles, User, Loader2, Bot } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

/**
 * Componente de Chat con IA Financiera - Rediseño Apple Intelligence Glass Glow
 */
const AIChat = ({ isOpen, onClose }) => {
  const { token } = useContext(AuthContext);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: '¡Hola! 👋 Soy tu asistente financiero inteligente. Puedo analizar tus gastos, resolver dudas sobre tus balances y darte consejos para ahorrar. ¿En qué te ayudo hoy?'
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Manejo de teclado móvil con visualViewport
  useEffect(() => {
    if (!isOpen) return;
    const vv = window.visualViewport;
    if (!vv) return;

    const onResize = () => {
      const kh = Math.max(0, window.innerHeight - vv.height - (vv.offsetTop || 0));
      setKeyboardHeight(kh);
      if (kh > 50) {
        setTimeout(() => scrollToBottom(), 100);
      }
    };

    vv.addEventListener('resize', onResize);
    vv.addEventListener('scroll', onResize);
    return () => {
      vv.removeEventListener('resize', onResize);
      vv.removeEventListener('scroll', onResize);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
    
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || loading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');

    const newMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMessages);
    setLoading(true);

    try {
      const conversationHistory = newMessages.slice(-6).map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const response = await axios.post(
        `${API}/ai/chat`,
        {
          message: userMessage,
          conversation_history: conversationHistory
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessages([...newMessages, {
        role: 'assistant',
        content: response.data.response
      }]);

    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Esta funcionalidad requiere suscripción Premium');
        onClose();
      } else {
        toast.error('Error al enviar mensaje');
        setMessages([...newMessages, {
          role: 'assistant',
          content: 'Lo siento, tuve un problema procesando tu mensaje. ¿Podrías intentar de nuevo?'
        }]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const quickQuestions = [
    "¿Cuánto gasté este mes?",
    "¿En qué categoría gasto más?",
    "¿Cómo puedo ahorrar más?",
    "¿Cuál es mi balance actual?"
  ];

  const handleQuickQuestion = (question) => {
    setInputMessage(question);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const touchStartY = useRef(null);

  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e) => {
    if (touchStartY.current === null) return;
    const touchEndY = e.changedTouches[0].clientY;
    const distance = touchEndY - touchStartY.current;
    if (distance > 50) {
      onClose();
    }
    touchStartY.current = null;
  };

  const handleClose = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div 
        style={{
          paddingBottom: keyboardHeight > 50 ? `${keyboardHeight}px` : undefined,
          transition: 'padding-bottom 0.2s ease-out'
        }}
        className="w-full h-full sm:h-[620px] sm:max-w-md flex flex-col bg-slate-50 sm:rounded-3xl shadow-2xl overflow-hidden border-0 sm:border border-indigo-100"
      >
        {/* Header con Safe Area para iPhone y gradiente Glass Glow */}
        <div 
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          className="relative bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 text-white px-5 pb-3 flex flex-col shadow-md flex-shrink-0 touch-none select-none"
          style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.5rem)' }}
        >
          {/* Handle visual de arrastre para cerrar en móvil */}
          <div className="sm:hidden flex justify-center pt-1 pb-2 cursor-grab active:cursor-grabbing">
            <div className="w-12 h-1.5 bg-white/40 rounded-full" />
          </div>

          {/* Luz sutil de fondo */}
          <div className="absolute top-0 inset-x-0 h-full bg-gradient-to-b from-white/15 to-transparent pointer-events-none" />

          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 rounded-2xl bg-white/15 backdrop-blur-md border border-white/30 flex items-center justify-center shadow-inner">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-400 border-2 border-indigo-700 rounded-full shadow-sm" />
              </div>
              <div>
                <h3 className="font-bold text-base leading-tight tracking-tight">Asistente IA</h3>
                <p className="text-[11px] text-purple-200 font-medium">Finanzas Inteligentes • En línea</p>
              </div>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
              className="text-white/90 hover:text-white hover:bg-white/20 rounded-full w-9 h-9 p-0 flex items-center justify-center transition-all active:scale-95 z-10"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Contenedor de Mensajes */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex items-end gap-2.5 ${
                message.role === 'user' ? 'flex-row-reverse' : ''
              }`}
            >
              {/* Avatar */}
              <div className={`flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center shadow-sm ${
                message.role === 'user'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gradient-to-br from-purple-500 to-indigo-600 text-white'
              }`}>
                {message.role === 'user' ? (
                  <User className="w-4 h-4" />
                ) : (
                  <Bot className="w-4 h-4" />
                )}
              </div>

              {/* Burbuja de Mensaje */}
              <div
                className={`max-w-[82%] px-4 py-3 text-[13.5px] leading-relaxed shadow-sm ${
                  message.role === 'user'
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-3xl rounded-br-sm shadow-indigo-500/20'
                    : 'bg-white text-slate-800 border border-slate-200/70 rounded-3xl rounded-bl-sm'
                }`}
              >
                <p className="whitespace-pre-wrap">{message.content}</p>
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex items-end gap-2.5">
              <div className="flex-shrink-0 w-8 h-8 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white shadow-sm">
                <Bot className="w-4 h-4" />
              </div>
              <div className="bg-white border border-slate-200/70 px-4 py-3 rounded-3xl rounded-bl-sm shadow-sm flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
                <span className="text-xs font-medium text-slate-500">Analizando tus finanzas...</span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Preguntas Frecuentes Rápidas (Chips tipo píldora) */}
        {messages.length <= 2 && (
          <div className="px-4 py-2.5 bg-white border-t border-slate-100/90 flex-shrink-0">
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">Sugerencias rápidas</p>
            <div className="flex flex-nowrap overflow-x-auto gap-2 pb-1 scrollbar-hide">
              {quickQuestions.map((question, index) => (
                <button
                  key={index}
                  onClick={() => handleQuickQuestion(question)}
                  className="whitespace-nowrap flex-shrink-0 text-[11px] font-semibold px-3.5 py-1.5 bg-indigo-50/70 hover:bg-indigo-100/80 text-indigo-700 rounded-full transition-all border border-indigo-100 active:scale-95 shadow-sm flex items-center gap-1.5"
                >
                  <Sparkles className="w-3 h-3 text-indigo-500" />
                  {question}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input Bar Flotante con Safe-Area inferior */}
        <div 
          className="p-3 bg-white border-t border-slate-100 flex-shrink-0"
          style={{ paddingBottom: keyboardHeight > 50 ? '0.75rem' : 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' }}
        >
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200/90 rounded-full px-4 py-1 focus-within:bg-white focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 transition-all shadow-inner">
            <input
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Pregunta sobre tus gastos o presupuesto..."
              disabled={loading}
              className="flex-1 bg-transparent border-0 text-xs sm:text-sm text-slate-800 focus:outline-none placeholder-slate-400 font-medium py-2"
            />
            <button
              onClick={sendMessage}
              disabled={loading || !inputMessage.trim()}
              className="w-9 h-9 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white flex items-center justify-center shadow-md active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed transition-all shrink-0 hover:shadow-indigo-500/30"
              aria-label="Enviar mensaje"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4 ml-0.5" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIChat;

